import type { Vec3 } from './assemblyPose';
import { keptHand, productMiddleX, sayOn } from './holeLines';
import { builtBoundsOf } from './openaiBlenderApi';
import { loadPlacedMesh, type Solid } from './partSolids';
import { isLeftRight, type PartItem } from './partsRun';
import { isCentred, type HoleAt, type StepHole } from './stepHoles';
import type { Job3D } from './threeDApi';

// WHERE EACH COVER GOES — joint by joint, in the component holes' view. Which cover goes on which place is asked of
// a model (see coverPlacementRun, cover_placement.py); this is what the page gives it and what it
// makes of the answer.
//
// THE PLACES A COVER COULD GO, each with an id, are what the page already knows: every hole Find
// holes placed, with what goes in it, and every foot of a part pads are laid on, read off its
// model. The model picks among them — and may move a cover within its place's face.
//
// EACH COVER IS SAID AS A HOLE IS — a part, a face, a place on it — so the joint view draws it the
// way it draws a screw in its hole (see JointHoles): at the wood's own surface, turned along the
// face's normal. A cover on a hole goes in as deep as it is long; one on the floor not at all.
export type CoverMark = StepHole & {
  cover: 'hole' | 'surface';
  // The place it is on: the hole it caps, or the floor spot.
  of?: string;
  // Turned over from the way its model was built to go (see Placement.flip).
  flip?: boolean;
  // Moved along its face's normal, out of the part — see Placement.outMm.
  outMm?: number;
};

// A FOOT, WHERE A PART STANDS ON THE FLOOR — F1, F2… — on one unit of it.
export type FloorSpot = { id: string; part: string; side?: 'left' | 'right'; face: 'bottom'; u: HoleAt; v: HoleAt };

// ONE COVER, WHERE THE ANSWER PUT IT: the cover's stored id, the place's id, a position within the
// place's face where it moved it, and whether it is turned over.
// `outMm` moves it along the face's normal, out of the part (positive) or further in.
// (`outMm` is an older answer's, kept to draw it: covers rest where they first touch the wood.)
export type Placement = { cover: string; on: string; u?: HoleAt; v?: HoleAt; outMm?: number; flip: boolean };

// THE HOLE A PIECE OF HARDWARE WENT IN BY — the one its line was named after (StepHole.line), or,
// on an answer from before lines were kept, the one it goes all the way through.
export const isEntry = (h: StepHole) => (h.line ? h.line === h.id : h.through);

// A position in the words the prompt uses: "80 mm from the left", or "centred".
export const atWords = (at: HoleAt) => (isCentred(at) ? 'centred' : `${at.mm} mm from the ${at.from}`);

// HOW CLOSE TO A PART'S UNDERSIDE A VERTEX MUST BE TO BE PART OF A FOOT, and how far apart two
// feet are at least: a foot's underside is a patch a few millimetres thick at most, and two legs
// are never closer than a couple of centimetres.
export const FLOOR_MM = 1.5;
export const FOOT_GAP_MM = 15;

// THE FEET OF ONE PART: the vertices of its model within FLOOR_MM of its lowest point, grouped where they
// are closer than FOOT_GAP_MM, each group's middle. In the product's frame, millimetres.
async function feetOf(job: Job3D, item: PartItem, floor: number): Promise<Vec3[]> {
  const placed = await loadPlacedMesh(job, item.size);
  if (!placed) return [];
  const { THREE, root } = placed;
  const low: [number, number][] = [];
  const p = new THREE.Vector3();
  root.traverse((o) => {
    const mesh = o as import('three').Mesh;
    if (!mesh.isMesh) return;
    const pos = mesh.geometry.getAttribute('position');
    for (let i = 0; i < pos.count; i++) {
      p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      if (p.z <= floor + FLOOR_MM) low.push([p.x, p.y]);
    }
  });
  // Grouped by nearness: each point joins the first group one of whose points is within reach.
  const groups: [number, number][][] = [];
  for (const q of low) {
    const near = groups.filter((g) => g.some((r) => Math.hypot(r[0] - q[0], r[1] - q[1]) < FOOT_GAP_MM));
    if (!near.length) { groups.push([q]); continue; }
    const into = near[0];
    into.push(q);
    for (const other of near.slice(1)) {
      into.push(...other);
      groups.splice(groups.indexOf(other), 1);
    }
  }
  return groups.map((g) => {
    const xs = g.map((q) => q[0]);
    const ys = g.map((q) => q[1]);
    return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2, floor] as Vec3;
  });
}

// EVERY FOOT OF THE PARTS PADS ARE LAID ON, on both hands of a pair — the places a pad could go.
// Read off each part's own underside, not the product's floor: a part placed a little high in the
// assembly still has its feet, and a pad is laid on the part the Hardware table names, whatever
// stands lowest. A part with no model to read has none.
export async function floorSpots(
  items: PartItem[],
  meshOf: (id: string) => Job3D | null,
  parts: string[],
): Promise<FloorSpot[]> {
  const mx = productMiddleX(items, meshOf);
  const out: FloorSpot[] = [];
  for (const part of parts) {
    const job = meshOf(part);
    const box = builtBoundsOf(job);
    if (!job || !box) continue;
    const feet = await feetOf(job, items[Number(part)], box.min[2]);
    const pair = isLeftRight(items[Number(part)]) && mx != null;
    const hands = pair ? [keptHand(box, mx!), keptHand(box, mx!) === 'left' ? 'right' : 'left'] as const : [undefined];
    for (const foot of feet) {
      const at = sayOn(foot, 'bottom', { min: box.min, max: box.max, of: 0 });
      for (const side of hands) {
        out.push({ id: `F${out.length + 1}`, part, face: 'bottom', u: at.u, v: at.v, ...(side ? { side } : {}) });
      }
    }
  }
  return out;
}

// THE ANSWER, AS COVERS THE JOINT VIEW DRAWS: each placement on its place, moved where it said. A
// placement whose place is gone (the holes found again since) is left out, and counted below.
export function coverMarks(
  placements: Placement[],
  holes: StepHole[],
  spots: FloorSpot[],
): CoverMark[] {
  return placements.flatMap((p): CoverMark[] => {
    const hole = holes.find((h) => h.id === p.on);
    const spot = hole ? null : spots.find((s) => s.id === p.on);
    const moved = {
      ...(p.u ? { u: p.u } : {}), ...(p.v ? { v: p.v } : {}), ...(p.outMm ? { outMm: p.outMm } : {}), flip: p.flip,
    };
    if (hole) {
      return [{
        ...hole, id: `${hole.id}·${p.cover}`, via: p.cover, line: undefined, through: false,
        // RESTING WHERE IT FIRST TOUCHES THE WOOD, coming in along its hole: measured on the part
        // under the whole of it (see JointHoles' mouths), not at the hole's one point — so nothing
        // of its own and nothing of the hole's goes deeper (see restDown, which reads these).
        surfaceMm: undefined, sunkMm: 0, depthMm: 0,
        diameterMm: null, cover: 'hole', of: hole.id, ...moved,
      }];
    }
    if (spot) {
      return [{
        id: `${spot.id}·${p.cover}`, at: '', part: spot.part, face: spot.face, u: spot.u, v: spot.v,
        ...(spot.side ? { side: spot.side } : {}), via: p.cover, diameterMm: null, depthMm: 0,
        through: false, cover: 'surface', of: spot.id, ...moved,
      }];
    }
    return [];
  });
}

// HOW FAR A COVER COMES IN BEFORE IT TOUCHES THE WOOD, from its place on the box face: rays along
// its way in, from its middle and from rings across its width, and the shortest — the first contact
// under any of it. A ray down a bore goes on; the rim round it stops the cover. Null where nothing
// is under it at all.
const RINGS = [0.5, 1];
const RAYS = 16;
export function firstContact(solid: Solid, at: Vec3, n: Vec3, reach: number): number | null {
  const a: Vec3 = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const u: Vec3 = [n[1] * a[2] - n[2] * a[1], n[2] * a[0] - n[0] * a[2], n[0] * a[1] - n[1] * a[0]];
  const ul = Math.hypot(...u);
  const e1: Vec3 = [u[0] / ul, u[1] / ul, u[2] / ul];
  const e2: Vec3 = [n[1] * e1[2] - n[2] * e1[1], n[2] * e1[0] - n[0] * e1[2], n[0] * e1[1] - n[1] * e1[0]];
  const offsets: [number, number][] = [[0, 0], ...RINGS.flatMap((k) => Array.from({ length: RAYS }, (_, i) => {
    const t = (2 * Math.PI * i) / RAYS;
    return [Math.cos(t) * reach * k, Math.sin(t) * reach * k] as [number, number];
  }))];
  let best: number | null = null;
  for (const [x, y] of offsets) {
    const from: Vec3 = [0, 1, 2].map((i) => at[i] + e1[i] * x + e2[i] * y + n[i] * 5) as unknown as Vec3;
    const hit = solid(from, [-n[0], -n[1], -n[2]])[0];
    if (hit != null && (best == null || hit - 5 < best)) best = hit - 5;
  }
  return best;
}
